import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEquipment } from '../../../context/EquipmentContext';
import { useTeam } from '../../../context/TeamContext';
import { useAuth } from '../../../context/AuthContext';
import { EQUIPMENT_TYPES, EQUIPMENT_STATUS } from '../../../data/nurseAppModels';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';

const EquipmentApprovalPage = () => {
  console.log('🚀 EquipmentApprovalPage: Component rendering');
  
  const navigate = useNavigate();
  const { equipment, approveEquipment, rejectEquipment, loading } = useEquipment();
  const { currentTeam } = useTeam();
  const { currentUser } = useAuth();

  const [pendingEquipment, setPendingEquipment] = useState([]);
  const [rejectedEquipment, setRejectedEquipment] = useState([]);
  const [activeTab, setActiveTab] = useState('pending'); // 'pending' | 'rejected'
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectingId, setRejectingId] = useState(null);
  const [approvingId, setApprovingId] = useState(null);

  const isLeader = currentTeam?.userRole === 'LEADER' || currentTeam?.userRole === 'DEPUTY_LEADER';

  // Debug logs on mount/update
  useEffect(() => {
    console.log('🔍 EquipmentApprovalPage: Component mounted/updated');
    console.log('  - currentTeam:', currentTeam);
    console.log('  - currentTeam?.id:', currentTeam?.id);
    console.log('  - currentTeam?.userRole:', currentTeam?.userRole);
    console.log('  - isLeader:', isLeader);
    console.log('  - currentUser:', currentUser?.uid);
  }, [currentTeam, isLeader, currentUser]);

  // Load pending and rejected equipment
  useEffect(() => {
    if (!currentTeam) {
      console.log('⚠️ EquipmentApprovalPage: No currentTeam');
      return;
    }

    const loadEquipment = async () => {
      try {
        console.log('🔍 EquipmentApprovalPage: Loading equipment for team:', currentTeam.id);
        
        // Load pending equipment - try with orderBy first, fallback to without orderBy
        let pendingData = [];
        try {
          const pendingQuery = query(
            collection(db, 'equipment'),
            where('teamId', '==', currentTeam.id),
            where('status', '==', EQUIPMENT_STATUS.PENDING_APPROVAL),
            orderBy('submittedAt', 'desc')
          );
          const pendingSnapshot = await getDocs(pendingQuery);
          pendingData = pendingSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          console.log('✅ EquipmentApprovalPage: Loaded pending equipment with orderBy:', pendingData.length);
        } catch (orderByError) {
          console.warn('⚠️ EquipmentApprovalPage: orderBy failed, trying without orderBy:', orderByError);
          // Fallback: query without orderBy
          const pendingQueryFallback = query(
            collection(db, 'equipment'),
            where('teamId', '==', currentTeam.id),
            where('status', '==', EQUIPMENT_STATUS.PENDING_APPROVAL)
          );
          const pendingSnapshotFallback = await getDocs(pendingQueryFallback);
          pendingData = pendingSnapshotFallback.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          // Sort manually by submittedAt or createdAt
          pendingData.sort((a, b) => {
            const aTime = a.submittedAt?.toDate?.() || a.createdAt?.toDate?.() || new Date(0);
            const bTime = b.submittedAt?.toDate?.() || b.createdAt?.toDate?.() || new Date(0);
            return bTime - aTime; // Descending
          });
          console.log('✅ EquipmentApprovalPage: Loaded pending equipment without orderBy:', pendingData.length);
        }
        setPendingEquipment(pendingData);

        // Load rejected equipment - try with orderBy first, fallback to without orderBy
        let rejectedData = [];
        try {
          const rejectedQuery = query(
            collection(db, 'equipment'),
            where('teamId', '==', currentTeam.id),
            where('status', '==', EQUIPMENT_STATUS.REJECTED),
            orderBy('rejectedAt', 'desc')
          );
          const rejectedSnapshot = await getDocs(rejectedQuery);
          rejectedData = rejectedSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          console.log('✅ EquipmentApprovalPage: Loaded rejected equipment with orderBy:', rejectedData.length);
        } catch (orderByError) {
          console.warn('⚠️ EquipmentApprovalPage: orderBy failed for rejected, trying without orderBy:', orderByError);
          // Fallback: query without orderBy
          const rejectedQueryFallback = query(
            collection(db, 'equipment'),
            where('teamId', '==', currentTeam.id),
            where('status', '==', EQUIPMENT_STATUS.REJECTED)
          );
          const rejectedSnapshotFallback = await getDocs(rejectedQueryFallback);
          rejectedData = rejectedSnapshotFallback.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          // Sort manually by rejectedAt or createdAt
          rejectedData.sort((a, b) => {
            const aTime = a.rejectedAt?.toDate?.() || a.createdAt?.toDate?.() || new Date(0);
            const bTime = b.rejectedAt?.toDate?.() || b.createdAt?.toDate?.() || new Date(0);
            return bTime - aTime; // Descending
          });
          console.log('✅ EquipmentApprovalPage: Loaded rejected equipment without orderBy:', rejectedData.length);
        }
        setRejectedEquipment(rejectedData);
      } catch (err) {
        console.error('❌ EquipmentApprovalPage: Error loading equipment:', err);
      }
    };

    loadEquipment();
  }, [currentTeam, equipment]);

  const handleApprove = async (equipmentId) => {
    if (!window.confirm('ยืนยันการอนุมัติอุปกรณ์นี้?')) return;

    try {
      setApprovingId(equipmentId);
      const result = await approveEquipment(equipmentId);
      
      if (result.success) {
        alert('✅ อนุมัติอุปกรณ์สำเร็จ');
        // Remove from pending list
        setPendingEquipment(prev => prev.filter(eq => eq.id !== equipmentId));
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error approving equipment:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (equipmentId) => {
    if (!rejectionReason.trim()) {
      alert('กรุณากรอกเหตุผลในการปฏิเสธ');
      return;
    }

    if (!window.confirm('ยืนยันการปฏิเสธอุปกรณ์นี้?')) return;

    try {
      setRejectingId(equipmentId);
      const result = await rejectEquipment(equipmentId, rejectionReason);
      
      if (result.success) {
        alert('❌ ปฏิเสธอุปกรณ์สำเร็จ');
        // Remove from pending list
        setPendingEquipment(prev => prev.filter(eq => eq.id !== equipmentId));
        setRejectionReason('');
        setRejectingId(null);
      } else {
        alert(`เกิดข้อผิดพลาด: ${result.error}`);
      }
    } catch (err) {
      console.error('Error rejecting equipment:', err);
      alert(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      setRejectingId(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case EQUIPMENT_STATUS.PENDING_APPROVAL:
        return <span className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded">⏳ รออนุมัติ</span>;
      case EQUIPMENT_STATUS.REJECTED:
        return <span className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded">❌ ถูกปฏิเสธ</span>;
      case EQUIPMENT_STATUS.AVAILABLE:
        return <span className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded">✅ อนุมัติแล้ว</span>;
      default:
        return null;
    }
  };

  if (!currentTeam) {
    console.log('⚠️ EquipmentApprovalPage: No currentTeam, showing "select team" message');
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">กรุณาเลือกทีมก่อน</p>
          <button
            onClick={() => navigate('/nurse/teams')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            ไปเลือกทีม
          </button>
        </div>
      </div>
    );
  }

  if (!isLeader) {
    console.log('⚠️ EquipmentApprovalPage: User is not a leader');
    console.log('  - currentTeam.userRole:', currentTeam.userRole);
    console.log('  - isLeader:', isLeader);
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">คุณไม่มีสิทธิ์เข้าถึงหน้านี้</p>
          <p className="text-sm text-gray-500 mb-4">คุณต้องเป็นหัวหน้าทีมหรือรองหัวหน้าทีม</p>
          <p className="text-xs text-gray-400 mb-4">Role ปัจจุบัน: {currentTeam.userRole || 'ไม่ระบุ'}</p>
          <button
            onClick={() => navigate('/nurse/equipment')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            กลับไปหน้าอุปกรณ์
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/nurse/equipment')}
            className="mb-4 text-blue-600 hover:text-blue-700 flex items-center gap-2"
          >
            <span>←</span> กลับ
          </button>
          <h1 className="text-2xl font-bold text-gray-800">อนุมัติอุปกรณ์</h1>
          <p className="text-sm text-gray-600 mt-1">
            รายการอุปกรณ์ที่รอการอนุมัติและถูกปฏิเสธ
          </p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="flex border-b border-gray-200">
            <button
              onClick={() => setActiveTab('pending')}
              className={`flex-1 px-4 py-3 text-center font-medium transition ${
                activeTab === 'pending'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              ⏳ รออนุมัติ ({pendingEquipment.length})
            </button>
            <button
              onClick={() => setActiveTab('rejected')}
              className={`flex-1 px-4 py-3 text-center font-medium transition ${
                activeTab === 'rejected'
                  ? 'text-blue-600 border-b-2 border-blue-600'
                  : 'text-gray-600 hover:text-gray-800'
              }`}
            >
              ❌ ถูกปฏิเสธ ({rejectedEquipment.length})
            </button>
          </div>
        </div>

        {/* Pending Equipment */}
        {activeTab === 'pending' && (
          <div className="space-y-4">
            {pendingEquipment.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <p className="text-gray-600">ไม่มีอุปกรณ์ที่รออนุมัติ</p>
              </div>
            ) : (
              pendingEquipment.map(eq => (
                <div key={eq.id} className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-gray-800">{eq.name}</h3>
                        {getStatusBadge(eq.status)}
                      </div>
                      <p className="text-sm text-gray-600 mb-1">รหัส: {eq.code}</p>
                      <p className="text-sm text-gray-600 mb-1">
                        ประเภท: {eq.type === EQUIPMENT_TYPES.SMALL_ITEM ? 'ชิ้นเล็ก' : 'ชิ้นใหญ่'}
                      </p>
                      {eq.category && (
                        <p className="text-sm text-gray-600 mb-1">หมวดหมู่: {eq.category}</p>
                      )}
                      {eq.unit && (
                        <p className="text-sm text-gray-600 mb-1">หน่วย: {eq.unit}</p>
                      )}
                      {eq.stockQuantity !== null && eq.stockQuantity !== undefined && (
                        <p className="text-sm text-gray-600 mb-1">
                          Stock ที่ตั้งไว้: {eq.stockQuantity} {eq.unit}
                        </p>
                      )}
                      {eq.createdByName && (
                        <p className="text-sm text-gray-500 mt-2">
                          สร้างโดย: {eq.createdByName}
                        </p>
                      )}
                      {eq.submittedAt && (
                        <p className="text-xs text-gray-400 mt-1">
                          ส่งเมื่อ: {eq.submittedAt?.toDate ? new Date(eq.submittedAt.toDate()).toLocaleString('th-TH') : 'ไม่ทราบ'}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 mt-4 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => handleApprove(eq.id)}
                      disabled={approvingId === eq.id}
                      className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
                    >
                      {approvingId === eq.id ? 'กำลังอนุมัติ...' : '✅ อนุมัติ'}
                    </button>
                    <button
                      onClick={() => {
                        setRejectingId(eq.id);
                        setRejectionReason('');
                      }}
                      disabled={rejectingId === eq.id}
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
                    >
                      ❌ ปฏิเสธ
                    </button>
                    <button
                      onClick={() => navigate(`/nurse/equipment/${eq.id}`)}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                    >
                      ดูรายละเอียด
                    </button>
                  </div>

                  {/* Rejection Form */}
                  {rejectingId === eq.id && (
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        เหตุผลในการปฏิเสธ *
                      </label>
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        rows={3}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 mb-3"
                        placeholder="กรุณากรอกเหตุผลในการปฏิเสธ..."
                      />
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleReject(eq.id)}
                          disabled={!rejectionReason.trim() || rejectingId !== eq.id}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition"
                        >
                          ยืนยันการปฏิเสธ
                        </button>
                        <button
                          onClick={() => {
                            setRejectingId(null);
                            setRejectionReason('');
                          }}
                          className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                        >
                          ยกเลิก
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Rejected Equipment */}
        {activeTab === 'rejected' && (
          <div className="space-y-4">
            {rejectedEquipment.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-8 text-center">
                <p className="text-gray-600">ไม่มีอุปกรณ์ที่ถูกปฏิเสธ</p>
              </div>
            ) : (
              rejectedEquipment.map(eq => (
                <div key={eq.id} className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-bold text-gray-800">{eq.name}</h3>
                        {getStatusBadge(eq.status)}
                      </div>
                      <p className="text-sm text-gray-600 mb-1">รหัส: {eq.code}</p>
                      {eq.rejectionReason && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm font-medium text-red-800 mb-1">เหตุผลที่ปฏิเสธ:</p>
                          <p className="text-sm text-red-700">{eq.rejectionReason}</p>
                        </div>
                      )}
                      {eq.rejectedByName && (
                        <p className="text-sm text-gray-500 mt-2">
                          ปฏิเสธโดย: {eq.rejectedByName}
                        </p>
                      )}
                      {eq.rejectedAt && (
                        <p className="text-xs text-gray-400 mt-1">
                          ปฏิเสธเมื่อ: {eq.rejectedAt?.toDate ? new Date(eq.rejectedAt.toDate()).toLocaleString('th-TH') : 'ไม่ทราบ'}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-3 mt-4 pt-4 border-t border-gray-200">
                    <button
                      onClick={() => navigate(`/nurse/equipment/${eq.id}`)}
                      className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
                    >
                      ดูรายละเอียด
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EquipmentApprovalPage;

